import hotkeys from "hotkeys-js";
import {
  AllFonts,
  BgColors,
  HotkeyScopes,
  Preferences,
  TextColors,
  CellIconNames,
  SubNavigations,
  TreeDefaultNodeIds,
} from "../../utils/constants";
import { tableTitleValidation } from "../../utils/util";
import { rangeIndexToLabel, splitCellId } from "../../components/process-design/cell/Utils";
import { NumberFormats } from "../../utils/DataTypeUtils";
import { I18n } from "aws-amplify";
import { Auth } from "aws-amplify";
import { navigateTo } from "../../router";
import { ProcessItemTypes, ProcessTypes } from "../../utils/icons";
import { ColumnTypes, RowTypes } from "../../components/process-design/cell/CellConstants";

const mapScopeToStore = {
  processes: "processes",
  players: "players",
  options: "sheets",
  grids: "grids",
  businesses: "businesses",
};

const functionMap = {
  1: { funcTitle: "toggleFavoriteProcess", parameters: [] },
  2: { funcTitle: "takeScreenshot", parameters: [] },
  5: { funcTitle: "toggleProcessFlow", parameters: [] },
  6: { funcTitle: "fullScreen", parameters: [] },
  7: { funcTitle: "copy", parameters: [] },
  8: { funcTitle: "cut", parameters: [] },
  9: { funcTitle: "paste", parameters: [] },
  12: { funcTitle: "merge", parameters: [] },
  13: { funcTitle: "addComment", parameters: [] },
  14: { funcTitle: "deleteComment", parameters: [] },
  17: { funcTitle: "goTo", parameters: [] },
  19: { funcTitle: "fillDirection", parameters: ["right"] },
  20: { funcTitle: "fillDirection", parameters: ["down"] },
  21: { funcTitle: "fillDirection", parameters: ["up"] },
  22: { funcTitle: "fillDirection", parameters: ["left"] },
  23: { funcTitle: "toggleSeriesDialogVisibility", parameters: [] },
  24: { funcTitle: "setNavigationCornerActive", parameters: [] },
  25: { funcTitle: "setDataViewActive", parameters: [] },
  26: { funcTitle: "bold", parameters: [] },
  27: { funcTitle: "clearStyle", parameters: [] },
  29: { funcTitle: "verticalAlign", parameters: ["top"] },
  30: { funcTitle: "verticalAlign", parameters: ["center"] },
  31: { funcTitle: "verticalAlign", parameters: ["bottom"] },
  32: { funcTitle: "textAlign", parameters: ["left"] },
  33: { funcTitle: "textAlign", parameters: ["center"] },
  34: { funcTitle: "textAlign", parameters: ["right"] },
  38: { funcTitle: "setLanguage", parameters: [0] },
  39: { funcTitle: "setLanguage", parameters: [1] },
  40: { funcTitle: "logout", parameters: [] },
  41: { funcTitle: "toggleProfileDialog", parameters: [] },
  42: { funcTitle: "toggleBussinessDialog", parameters: [] },
  43: { funcTitle: "addRow", parameters: [] },
  44: { funcTitle: "deleteRow", parameters: [] },
  45: { funcTitle: "moveRowUp", parameters: [] },
  46: { funcTitle: "moveRowDown", parameters: [] },
  47: { funcTitle: "addArrow", parameters: [] },
  48: { funcTitle: "deleteArrow", parameters: [] },
  83: { funcTitle: "addColumn", parameters: [] },
  49: { funcTitle: "deleteColumn", parameters: [] },
  50: { funcTitle: "moveColumnRight", parameters: [] },
  51: { funcTitle: "moveColumnLeft", parameters: [] },
  52: { funcTitle: "showPlayersTab", parameters: [] },
  53: { funcTitle: "showProcessesTab", parameters: [] },
  54: { funcTitle: "showTeamsTab", parameters: [] },
  55: { funcTitle: "showSheetsTab", parameters: [] },
  56: { funcTitle: "addSheet", parameters: [] },
  57: { funcTitle: "showSettingsTab", parameters: [] },
  58: { funcTitle: "showRedZone", parameters: [] },
  59: { funcTitle: "showGameZone", parameters: [] },
  60: { funcTitle: "newNormalProcess", parameters: [] },
  61: { funcTitle: "newPostingProcess", parameters: [] },
  62: { funcTitle: "newGrid", parameters: [] },
  63: { funcTitle: "open", parameters: [] },
  64: { funcTitle: "newFolder", parameters: [] },
  65: { funcTitle: "renameProcess", parameters: [] },
  66: { funcTitle: "deleteSidebarItem", parameters: [] },
  67: { funcTitle: "expandItem", parameters: [] },
  68: { funcTitle: "showTrash", parameters: [] },
  79: { funcTitle: "hideTrash", parameters: [] },
  69: { funcTitle: "restore", parameters: [] },
  70: { funcTitle: "newPlayer", parameters: [] },
  71: { funcTitle: "renamePlayer", parameters: [] },
  72: { funcTitle: "editSheet", parameters: [] },
  73: { funcTitle: "fontSize", parameters: [] },
  74: { funcTitle: "width", parameters: [] },
  75: { funcTitle: "height", parameters: [] },
  76: { funcTitle: "addPlayer", parameters: [] },
  77: { funcTitle: "editPlayer", parameters: [] },
  78: { funcTitle: "addView", parameters: [] },
  80: { funcTitle: "hideRow", parameters: [] },
  81: { funcTitle: "hideColumn", parameters: [] },
  84: { funcTitle: "autoLayout", parameters: [] },
  85: { funcTitle: "toggleProcessFlowLines", parameters: [] },
  86: { funcTitle: "addProcessFlowArrow", parameters: [] },
  87: { funcTitle: "changeCircleType", parameters: [] },
  88: { funcTitle: "openTable", parameters: [] },
  89: { funcTitle: "toggleGridlines", parameters: [] },
  90: { funcTitle: "setDataType", parameters: [NumberFormats.General] },
  91: { funcTitle: "setDataType", parameters: [NumberFormats.Number] },
  92: { funcTitle: "setDataType", parameters: [NumberFormats.Currency] },
  93: { funcTitle: "setDataType", parameters: [NumberFormats.ShortDate] },
  94: { funcTitle: "setDataType", parameters: [NumberFormats.LongDate] },
  95: { funcTitle: "setDataType", parameters: [NumberFormats.Time] },
  96: { funcTitle: "setDataType", parameters: [NumberFormats.Fraction] },
  97: { funcTitle: "setDataType", parameters: [NumberFormats.Text] },
  98: { funcTitle: "setDataType", parameters: [NumberFormats.Percentage] },
  99: { funcTitle: "openNameManager", parameters: [] },
  100: { funcTitle: "openAddNameRange", parameters: [] },
  101: { funcTitle: "addProcessFromCycle", parameters: [] },
  102: { funcTitle: "renameTable", parameters: [] },
  103: { funcTitle: "toggleAutoAppend", parameters: [] },
  104: { funcTitle: "toggleIsColumnFormula", parameters: [] },
  105: { funcTitle: "sortAsc", parameters: [] },
  106: { funcTitle: "sortDesc", parameters: [] },
  107: { funcTitle: "filter", parameters: [] },
  108: { funcTitle: "openFilter", parameters: [] },
  109: { funcTitle: "clearFilter", parameters: [] },
  110: { funcTitle: "formulaBox", parameters: [] },
  111: { funcTitle: "blueZone", parameters: [] },
  112: { funcTitle: "showGridsTab", parameters: [] },
  113: { funcTitle: "showBusinessTab", parameters: [] },
  114: { funcTitle: "fetchGridData", parameters: [] },
  115: { funcTitle: "duplicateSheet", parameters: [] },
  116: { funcTitle: "unmerge", parameters: [] },
  117: { funcTitle: "toggleNameRangeVisibility", parameters: [] },
  118: { funcTitle: "startStyles", parameters: [] },
  119: { funcTitle: "loadSatellite", parameters: [] },
  120: { funcTitle: "transferToGrid", parameters: [] },
};

export class SmartSearchHelper {
  constructor(rootState, rootGetters, dispatch, item, i18n) {
    this.rootState = rootState;
    this.rootGetters = rootGetters;
    this.dispatch = dispatch;
    this.i18n = i18n;

    this.item = item;
    const option = this.item.id.split("_");
    this.mode = option[0];
    this.funcId = option[1];
    this.setProperties();
  }
  callFunc() {
    if (this.mode === "func") {
      if (!functionMap[this.funcId]) return;
      this[functionMap[this.funcId].funcTitle](...functionMap[this.funcId].parameters);
    } else if (this.mode === "view") {
      this.setActiveView(this.funcId);
    } else if (this.mode === "style") {
      this.setStyle(+this.funcId);
    } else if (this.mode === "font") {
      this.setFontFamily(+this.funcId);
    } else if (this.mode === "color") {
      this.setColor(+this.funcId);
    } else if (this.mode === "bgColor") {
      this.setBgColor(+this.funcId);
    } else if (this.mode === "icon") {
      this.addIcon(+this.funcId);
    } else if (this.mode === "player") {
      this.showPlayer(this.funcId);
    } else if (["process", "grid", "business"].includes(this.mode)) {
      this.openDesign(this.mode, this.funcId);
    } else if (this.mode === "team" || this.mode === "member") {
      this.showTeamOrMember(this.funcId);
    } else if (this.mode === "sheet") {
      this.openSheet(this.funcId);
    } else if (this.mode === "circle") {
      this.changeCircleType(this.funcId);
    }
  }
  setProperties() {
    this.current = this.rootState.cells.current;

    this.designId = this.current?.id;

    this.activeCellId = this.rootState.cells.activeCellId;
    this.mergedCellIds = this.rootState.cells.mergedCellIds;
    this.cellsMetaData = this.current?.design.cellsMetaData;

    this.rowMap = this.current?.design.rowMap;
    this.columnMap = this.current?.design.columnMap;
    this.rows = this.current?.design.rows;
    this.columns = this.current?.design.columns;
  }
  // Helper Functions
  isFlowEditorVisible() {
    return this.rootGetters["cells/getColumnVisibility"]("Flow");
  }
  getFirstCellId(cellId) {
    return this.mergedCellIds[cellId] ? this.mergedCellIds[cellId].replace("_merged", "") : cellId;
  }
  showDialog(summary) {
    const message = { severity: "error", life: 3000, summary };
    this.dispatch("addToast", message, { root: true });
  }
  // Methods
  toggleFavoriteProcess() {
    this.dispatch(
      "preferences/toggleFavorites",
      {
        type: Preferences.FavoriteProcesses,
        ids: this.designId,
      },
      { root: true }
    );
  }
  toggleProcessFlow() {
    const id = "Show_Flow";
    if (this.isFlowEditorVisible()) this.dispatch("cells/editCellContent", { id, value: "0" }, { root: true });
    else this.dispatch("cells/editCellContent", { id, value: "" }, { root: true });
  }
  toggleSeriesDialogVisibility() {
    this.dispatch("views/setSeriesDialogVisibility", true, { root: true });
  }
  copy() {
    hotkeys.trigger("ctrl+c", HotkeyScopes.ProcessDesigner);
  }
  cut() {
    hotkeys.trigger("ctrl+x", HotkeyScopes.ProcessDesigner);
  }
  paste() {
    hotkeys.trigger("ctrl+v", HotkeyScopes.ProcessDesigner);
  }
  addComment() {
    const cellId = this.getFirstCellId(this.activeCellId);
    const activeCell = this.cellsMetaData[cellId] || {};

    const hasComment = "comment" in activeCell;
    if (this.activeCellId && !hasComment) this.dispatch("cells/addComment", null, { root: true });
    else if (hasComment) this.dispatch("cells/setComment", { id: cellId, editMode: true }, { root: true });
  }
  deleteComment() {
    this.dispatch("cells/removeComment", null, { root: true });
  }
  fullScreen() {
    const contentFocused = this.rootState.pdesign.contentFocused;
    this.dispatch("pdesign/setContentFocus", !contentFocused, { root: true });
  }
  merge() {
    const mainRange = this.rootGetters["cells/mainRange"];
    if (!mainRange) return false;

    const editMode = this.rootState.pdesign.editMode.value;
    this.dispatch("pdesign/setEditMode", editMode, { root: true });
    this.dispatch("cells/merge", { merge: false }, { root: true });
  }
  unmerge() {
    const mainRange = this.rootGetters["cells/mainRange"];
    if (!mainRange) return false;

    const editMode = this.rootState.pdesign.editMode.value;
    this.dispatch("pdesign/setEditMode", editMode, { root: true });
    this.dispatch("cells/unmerge", { merge: true }, { root: true });
  }
  goTo() {
    this.dispatch("pdesign/setGotoBox", true, { root: true });
  }
  blueZone() {
    this.dispatch("pdesign/toggleBlueZone", null, { root: true });
  }
  formulaBox() {
    this.dispatch("pdesign/toggleFormulaBox", null, { root: true });
  }
  fillDirection(direction) {
    const mainRange = this.rootGetters["cells/mainRange"];
    this.dispatch("cells/fillDirection", { range: mainRange.range, direction }, { root: true });
  }
  fillRight() {
    const mainRange = this.rootGetters["cells/mainRange"];
    this.dispatch("cells/fillDirection", { range: mainRange.range, direction: "right" }, { root: true });
  }
  fillLeft() {
    const mainRange = this.rootGetters["cells/mainRange"];
    this.dispatch("cells/fillDirection", { range: mainRange.range, direction: "left" }, { root: true });
  }
  fillUp() {
    const mainRange = this.rootGetters["cells/mainRange"];
    this.dispatch("cells/fillDirection", { range: mainRange.range, direction: "up" }, { root: true });
  }
  fillDown() {
    const mainRange = this.rootGetters["cells/mainRange"];
    this.dispatch("cells/fillDirection", { range: mainRange.range, direction: "down" }, { root: true });
  }
  setActiveView(id) {
    this.dispatch("views/setActiveView", id, { root: true });
  }
  setNavigationCornerActive() {
    this.dispatch("views/setActiveCorner", 1, { root: true });
  }
  setDataViewActive() {
    this.dispatch("views/setActiveCorner", 0, { root: true });
  }
  setStyle(index) {
    const styles = this.rootState.preferences.allPreferences.styles;
    if (!styles[index]) return;
    this.dispatch("rangeStyle/updateRangeStyle", { data: styles[index] }, { root: true });
  }
  bold() {
    const range = this.rootGetters["cells/mainRange"].range;
    const firstCellStyle = this.rootGetters["rangeStyle/firstCellStyle"](range[0], range[1]);
    const fontWeight = firstCellStyle?.fontWeight === "bold" ? "normal" : "bold";
    this.dispatch("rangeStyle/updateRangeStyle", { data: { fontWeight } }, { root: true });
  }
  clearStyle() {
    this.dispatch("rangeStyle/clearStyleFormats", null, { root: true });
  }
  setFontFamily(index) {
    const fontFamily = AllFonts[index];
    if (!fontFamily) return;
    this.dispatch("rangeStyle/updateRangeStyle", { data: { fontFamily } }, { root: true });
  }
  verticalAlign(align) {
    this.dispatch("rangeStyle/updateRangeStyle", { data: { verticalAlign: align } }, { root: true });
  }
  textAlign(align) {
    this.dispatch("rangeStyle/updateRangeStyle", { data: { textAlign: align } }, { root: true });
  }
  setColor(index) {
    const color = TextColors[index];
    if (!color) return;
    this.dispatch("rangeStyle/updateRangeStyle", { data: { color } }, { root: true });
  }
  setBgColor(index) {
    const bgColor = BgColors[index];
    if (!bgColor) return;
    this.dispatch("rangeStyle/updateRangeStyle", { data: { bgColor: bgColor.code } }, { root: true });
  }
  addIcon(index) {
    const icon = CellIconNames[index];
    if (!icon) return;
    this.dispatch("cells/updateIcon", icon, { root: true });
  }
  async takeScreenshot() {
    const copyToClipboard = async (imgDataUrl) => {
      const response = await fetch(imgDataUrl);
      const blob = await response.blob();

      navigator.clipboard.write([
        new window.ClipboardItem({
          [blob.type]: blob,
        }),
      ]);
    };
    const parent = document.querySelector(".toolbar_screenshot.toolbar-icon")?.closest(".kk-designer__component-item"); // Adjust the selector as needed
    if (parent) parent.style.pointerEvents = "none";

    // loading loader while taking screenshot.
    if (this.isLoading) return;
    this.isLoading = true;
    //load the library
    const html2canvas = (await import("html2canvas")).default;
    const node = document.querySelector(".kk-designer__design");
    // Remove add-Activity Button
    var addActivityBtn = document.querySelector(".kk-designer__grid-activitybtn");
    // Find the correct parent element
    if (addActivityBtn) {
      var addActivityParent = addActivityBtn.closest(".kk-designer__grid");
      if (addActivityParent) {
        addActivityParent.removeChild(addActivityBtn);
      }
    }
    // delete horizontal and vertical scrolls
    const scrollLeftAmount = node.closest(".kk-designer").scrollLeft;
    const scrollTopAmount = node.closest(".kk-designer").scrollTop;
    node.closest(".kk-designer").scrollLeft = 0;
    node.closest(".kk-designer").scrollTop = 0;

    const imageTitle = this.current.title;
    html2canvas(node, { scale: 3 }).then((canvas) => {
      // Convert the canvas to a data URL
      var imgData = canvas.toDataURL();

      // Create a link element with the image data as its href attribute
      var link = document.createElement("a");
      link.download = `${imageTitle}.png`;
      link.href = imgData;

      // Programmatically click on the link to download the image
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // copy the image to the clipboard
      copyToClipboard(imgData);
      this.isLoading = false;
      if (parent?.style) parent.style.pointerEvents = "";
    });
    // reset the changes.
    if (addActivityBtn) {
      addActivityParent.appendChild(addActivityBtn);
    }
    node.style.removeProperty("padding");
    node.closest(".kk-designer").scrollLeft = scrollLeftAmount;
    node.closest(".kk-designer").scrollTop = scrollTopAmount;
  }
  setLanguage(index) {
    const items = [
      { code: "en_US", title: "English", func: this.changeLocale, imgClass: "kk-language__flag", id: 0 },
      { code: "de", title: "Deutsch", func: this.changeLocale, imgClass: "kk-language__flag", id: 1 },
    ];
    this.i18n.locale = items[index].code;
    I18n.setLanguage(items[index].code);
    localStorage.setItem("userLanguage", JSON.stringify(items[index]));
  }
  logout() {
    Auth.signOut().catch((err) => {
      console.error(err);

      this.dispatch(
        "addToast",
        {
          severity: "error",
          life: 3000,
          ...this.i18n.tm("signout.failed"),
          ...{ detail: err.message },
        },
        { root: true }
      );
    });
  }
  toggleProfileDialog() {
    this.dispatch("toggleProfileDialog", undefined, { root: true });
  }
  toggleNameRangeVisibility() {
    this.dispatch("table/toggleNameRanbeVisibility", undefined, { root: true });
  }
  toggleBussinessDialog() {
    this.dispatch("toggleBussinessDialog", undefined, { root: true });
  }
  addRow() {
    const range = this.rootGetters["cells/activeRange"]().range;
    const rowIds = this.rootGetters["cells/rowsInRange"](range);
    let firstRowGroupIndex = this.rows.findIndex(
      (item) => item.type === RowTypes.RowGroup || item.type === RowTypes.PlayerAttribute
    );
    let firstActivityIndex = this.rows.findIndex((item) => item.type === RowTypes.Activity);
    const isRowGroupInRange = rowIds.some((id) => this.rowMap[id].type === RowTypes.RowGroup);
    const row = this.rows[range[1]];
    let index = this.rowMap[row.id].index;
    if (this.current.type === ProcessTypes.Grid) {
      const count = this.rootGetters["cells/rowsInRange"](range).length;
      this.dispatch("cells/addRow", { index: range[1], count, type: RowTypes.Grid }, { root: true });
    } else if (isRowGroupInRange || index < firstRowGroupIndex) {
      if (index < firstRowGroupIndex) index = firstRowGroupIndex;
      const count = rowIds.filter((id) => this.rowMap[id].type === RowTypes.RowGroup).length || 1;
      this.dispatch(
        "cells/addRow",
        { type: RowTypes.RowGroup, customRange: [range[0], index, range[2], index + count - 1] },
        { root: true }
      );
    } else {
      if (firstActivityIndex < 0) firstActivityIndex = this.rows.length;
      if (index < firstActivityIndex) index = firstActivityIndex;

      const count = rowIds.filter((id) => this.rowMap[id].type === RowTypes.Activity).length || 1;
      this.dispatch(
        "cells/addRow",
        { type: RowTypes.Activity, customRange: [range[0], index, range[2], index + count - 1] },
        { root: true }
      );
    }
  }
  addColumn() {
    const range = this.rootGetters["cells/activeRange"]().range;
    const count = this.rootGetters["cells/columnsInRange"](range).length;
    this.dispatch("cells/addColumn", { index: range[0], count, type: ColumnTypes.Grid }, { root: true });
  }
  deleteRow() {
    this.dispatch("cells/removeRows", {}, { root: true });
  }
  async moveRowUp() {
    const range = this.rootGetters["cells/activeRange"]().range;
    range[0] = 0;
    range[2] = this.columns.length - 1;
    await this.dispatch(
      "cells/setFormulaRange",
      { title: rangeIndexToLabel(range), isMainRange: true, activeCellId: this.activeCellId },
      { root: true }
    );
    setTimeout(() => {
      hotkeys.trigger("ctrl+up", HotkeyScopes.ProcessDesigner);
    }, 0);
  }
  async moveRowDown() {
    const range = this.rootGetters["cells/activeRange"]().range;
    range[0] = 0;
    range[2] = this.columns.length - 1;
    await this.dispatch(
      "cells/setFormulaRange",
      { title: rangeIndexToLabel(range), isMainRange: true, activeCellId: this.activeCellId },
      { root: true }
    );
    setTimeout(() => {
      hotkeys.trigger("ctrl+down", HotkeyScopes.ProcessDesigner);
    }, 0);
  }
  addArrow() {
    const parameterIds = this.rootState.smartSearch.parameterIds.map((item) => item.id);
    const range = this.rootGetters["cells/activeRange"]().range;
    const row = this.rows[range[1]];

    const startColumnId = parameterIds[0].split("_")[1];
    let endColumnId = parameterIds[1].split("_")[1];

    const plusToMinus = startColumnId?.includes("Plus") && endColumnId?.includes("Minus");
    endColumnId = plusToMinus ? endColumnId.replace("Minus", "Plus") : endColumnId;
    if (row.id && startColumnId && endColumnId)
      this.dispatch("cells/addArrow", { rowId: row.id, startColumnId, endColumnId }, { root: true });
  }
  deleteArrow() {
    const range = this.rootGetters["cells/activeRange"]().range;
    const row = this.rows[range[1]];
    if (!this.rootState.cells.selectedArrow)
      return this.showDialog(this.i18n.t("processDesigner.arrow.noSelectedArrow"));

    if (this.rootState.cells.selectedArrow?.rowId !== row.id)
      return this.showDialog(this.i18n.t("processDesigner.arrow.notSameArrow"));

    this.dispatch("cells/deleteSelectedArrow", null, { root: true });
  }
  deleteColumn() {
    const range = this.rootGetters["cells/activeRange"]().range;
    let columnIds = this.rootGetters["cells/columnsInRange"](range);

    const isColumnGroupInRange = columnIds.some((id) => this.columnMap[id].type === ColumnTypes.ColumnGroup);
    const isPlayerInRange = columnIds.some((id) => this.columnMap[id].type === ColumnTypes.Player);

    if (isColumnGroupInRange) columnIds = columnIds.filter((id) => this.columnMap[id].type === ColumnTypes.ColumnGroup);
    else if (isPlayerInRange) columnIds = columnIds.filter((id) => this.columnMap[id].type === ColumnTypes.Player);
    else columnIds = columnIds.filter((id) => this.columnMap[id].type === ColumnTypes.Grid);

    const set = new Set();

    columnIds.forEach((columnId) => {
      if (columnId.includes("-Plus") || columnId.includes("-Minus")) {
        const id = columnId.replace("-Plus", "").replace("-Minus", "");
        set.add(`${id}-Plus`);
        set.add(`${id}-Minus`);
      } else {
        set.add(columnId);
      }
    });

    this.dispatch("cells/removeColumns", [...set], { root: true });
  }
  async moveColumnRight() {
    hotkeys.trigger("ctrl+space", HotkeyScopes.ProcessDesigner);
    setTimeout(() => {
      hotkeys.trigger("ctrl+right", HotkeyScopes.ProcessDesigner);
    }, 0);
  }
  async moveColumnLeft() {
    hotkeys.trigger("ctrl+space", HotkeyScopes.ProcessDesigner);
    setTimeout(() => {
      hotkeys.trigger("ctrl+left", HotkeyScopes.ProcessDesigner);
    }, 0);
  }
  showStylesTab() {
    this.dispatch("preferences/update", { key: "activeSubNavigation", value: SubNavigations.Styles }, { root: true });
  }
  showPlayersTab() {
    this.dispatch("preferences/update", { key: "activeSubNavigation", value: SubNavigations.Players }, { root: true });
    this.dispatch("smartSearch/setSmartSearchPrevScope", HotkeyScopes.Sidebar.Players, { root: true });
    this.dispatch("setScope", SubNavigations.Players, { root: true }); // for toggling active scope. ctrl+\
  }
  showPlayer(playerId) {
    this.dispatch("preferences/update", { key: "activeSubNavigation", value: SubNavigations.Players }, { root: true });
    this.dispatch("players/setActiveItem", { id: playerId }, { root: true });
    this.dispatch("players/bringIntoView", { id: playerId }, { root: true });
    setTimeout(() => {
      document.querySelector(`.kk-tree__title[data-id = "${playerId}"]`)?.scrollIntoView({ block: "center" });
    }, 0);
    this.dispatch("smartSearch/setSmartSearchPrevScope", HotkeyScopes.Sidebar.Players, { root: true });
    this.dispatch("setScope", SubNavigations.Players, { root: true }); // for toggling active scope. ctrl+\
  }
  showProcessesTab() {
    this.dispatch(
      "preferences/update",
      { key: "activeSubNavigation", value: SubNavigations.Processes },
      { root: true }
    );
    this.dispatch("smartSearch/setSmartSearchPrevScope", HotkeyScopes.Sidebar.Processes, { root: true });
    this.dispatch("setScope", SubNavigations.Processes, { root: true }); // for toggling active scope. ctrl+\
  }
  showGridsTab() {
    this.dispatch("preferences/update", { key: "activeSubNavigation", value: SubNavigations.Grids }, { root: true });
    this.dispatch("smartSearch/setSmartSearchPrevScope", HotkeyScopes.Sidebar.Grids, { root: true });
    this.dispatch("setScope", SubNavigations.Grids, { root: true }); // for toggling active scope. ctrl+\
  }
  showBusinessTab() {
    this.dispatch(
      "preferences/update",
      { key: "activeSubNavigation", value: SubNavigations.Businesses },
      { root: true }
    );
    this.dispatch("smartSearch/setSmartSearchPrevScope", HotkeyScopes.Sidebar.Businesses, { root: true });
    this.dispatch("setScope", SubNavigations.Businesses, { root: true }); // for toggling active scope. ctrl+\
  }
  fetchGridData() {
    this.dispatch("cells/fetchGridData", null, { root: true });
  }
  async transferToGrid() {
    if (!this.rootState.cells.current || this.rootState.cells.current.type !== ProcessTypes.Cycle) return;

    localStorage.setItem("sapData", JSON.stringify(this.rootState.cells.current.sapData));
    this.showRedZone();
    this.showGridsTab();
    this.dispatch("grids/setActiveItem", { id: "PRIVATE", itemId: "PRIVATE" }, { root: true });
    this.newGrid();
    this.dispatch("pdesign/setCycleToGrid", true, { root: true });
  }

  openDesign(mode, id) {
    const map = {
      process: "processes",
      grid: "grids",
      business: "businesses",
    };
    this.dispatch(`${map[mode]}/open`, id, { root: true });
  }
  showTeamsTab() {
    this.dispatch("preferences/update", { key: "activeSubNavigation", value: SubNavigations.Teams }, { root: true });
    this.dispatch("smartSearch/setSmartSearchPrevScope", HotkeyScopes.Sidebar.Teams, { root: true });
    this.dispatch("setScope", SubNavigations.Teams, { root: true }); // for toggling active scope. ctrl+\
  }
  showTeamOrMember(id) {
    this.dispatch("preferences/update", { key: "activeSubNavigation", value: SubNavigations.Teams }, { root: true });
    this.dispatch("teams/setActiveItem", { id }, { root: true });
    this.dispatch("teams/bringIntoView", { id }, { root: true });
    setTimeout(() => {
      document.querySelector(`.kk-tree__title[data-id = "${id}"]`)?.scrollIntoView({ block: "center" });
    }, 0);
    this.dispatch("smartSearch/setSmartSearchPrevScope", HotkeyScopes.Sidebar.Teams, { root: true });
    this.dispatch("setScope", SubNavigations.Teams, { root: true }); // for toggling active scope. ctrl+\
  }
  showSheetsTab() {
    this.dispatch("preferences/update", { key: "activeSubNavigation", value: SubNavigations.Options }, { root: true });
    this.dispatch("smartSearch/setSmartSearchPrevScope", HotkeyScopes.Sidebar.Options, { root: true });
    this.dispatch("setScope", SubNavigations.Options, { root: true }); // for toggling active scope. ctrl+\
  }
  openSheet(sheetIndex) {
    const sheetId = this.rootState.sheets.allSheets[sheetIndex].id;
    this.dispatch("sheets/openSheet", sheetId, { root: true });
  }
  addSheet() {
    if (this.rootState.cells?.current.type !== ProcessTypes.Grid) return;
    const activeSheet = this.rootState.sheets.activeSheet;
    this.dispatch("sheets/addSheet", activeSheet.id, { root: true });
  }
  duplicateSheet() {
    if (this.rootState.cells?.current.type !== ProcessTypes.Grid) return;
    const activeSheet = this.rootState.sheets.activeSheet;
    this.dispatch("sheets/duplicateSheet", activeSheet.id, { root: true });
  }
  showSettingsTab() {
    this.dispatch("preferences/update", { key: "activeSubNavigation", value: SubNavigations.Settings }, { root: true });
    this.dispatch("smartSearch/setSmartSearchPrevScope", HotkeyScopes.MoreCommands, { root: true });
    this.dispatch("setScope", SubNavigations.Settings, { root: true }); // for toggling active scope. ctrl+\
  }
  showRedZone() {
    const tabs = {
      [SubNavigations.Settings]: () => this.showSettingsTab(),
      [SubNavigations.Processes]: () => this.showProcessesTab(),
      [SubNavigations.Grids]: () => this.showGridsTab(),
      [SubNavigations.Businesses]: () => this.showBusinessTab(),
      [SubNavigations.Options]: () => this.showSheetsTab(),
      [SubNavigations.Teams]: () => this.showTeamsTab(),
      [SubNavigations.Players]: () => this.showPlayersTab(),
      [SubNavigations.Styles]: () => this.showStylesTab(),
    };
    const activeTab = this.rootState.preferences.allPreferences?.activeSubNavigation;
    tabs[activeTab]();
    this.dispatch("setScope", activeTab, { root: true }); // for toggling active scope. ctrl+\
  }
  showGameZone() {
    this.dispatch("smartSearch/setSmartSearchPrevScope", HotkeyScopes.ProcessDesigner, { root: true });
    this.dispatch("setScope", HotkeyScopes.ProcessDesigner, { root: true }); // for toggling active scope. ctrl+\
  }
  newNormalProcess() {
    const item = this.getActiveItem();

    if (!item.id) return;
    this.dispatch(
      `${item.store}/addProcess`,
      {
        id: item.id,
        type: ProcessItemTypes.Normal,
      },
      { root: true }
    );
  }
  newPostingProcess() {
    const item = this.getActiveItem();

    if (!item.id) return;
    this.dispatch(
      `${item.store}/addProcess`,
      {
        id: item.id,
        type: ProcessItemTypes.Posting,
      },
      { root: true }
    );
  }
  newGrid() {
    const item = this.getActiveItem();
    if (!item.id) return;
    this.dispatch(`${item.store}/addGrid`, item.id, { root: true });
  }
  open() {
    const item = this.getActiveItem();
    if (!["normal", "posting", "grid"].includes(item.type)) return;
    this.dispatch(`${item.store}/open`, item.id, { root: true });
  }
  newFolder() {
    const item = this.getActiveItem();
    if (!item.id) return;
    this.dispatch(`${item.store}/addFolder`, item.id, { root: true });
  }
  getActiveItem() {
    const activeSubNavigation = this.rootState.preferences.allPreferences?.activeSubNavigation;
    const store = mapScopeToStore[activeSubNavigation];

    const map = {
      processes: "activeProcess",
      players: "activePlayer",
      options: "activeSheet",
      grids: "activeGrid",
      businesses: "activeBusiness",
    };
    const item = this.rootState[store][map[activeSubNavigation]];
    const tree = this.rootState[store].tree;
    const type = tree.getNode(tree.allData, item.id)?.type;

    return { id: item.id, itemId: item.itemId, type, store };
  }
  renameProcess() {
    const item = this.getActiveItem();
    if (!["normal", "posting", "grid"].includes(item.type)) return;

    this.dispatch(`${item.store}/setActiveItem`, { id: item.id, editMode: true }, { root: true });
  }
  deleteSidebarItem() {
    const prevScope = this.rootState.smartSearch.smartSearchPrevScope;
    const activeSubNavigation = this.rootState.preferences.allPreferences?.activeSubNavigation;
    const store = mapScopeToStore[activeSubNavigation];

    if (prevScope === HotkeyScopes.Trash) {
      if (!["businesses", "grids", "processes", "players", "options"].includes(activeSubNavigation)) return;
      const ids = this.rootState[store].selectedTrashItems;
      this.dispatch(`${store}/setSelectedTrashItems`, [], { root: true });

      this.dispatch(`${store}/remove`, { ids }, { root: true });
    } else if (["processes", "players", "options", "grids", "businesses"].includes(activeSubNavigation)) {
      const { id, itemId } = this.getActiveItem();
      if (["grids", "processes", "businesses"].includes(activeSubNavigation) && this.designId == itemId) {
        navigateTo("/designs");
        this.dispatch("cells/resetFormulaRange", null, { root: true });
      }
      this.dispatch(`${store}/remove`, { ids: [id] }, { root: true });
    }
  }
  expandItem() {
    const item = this.getActiveItem();
    if (!item.id) return;
    this.dispatch(`${item.store}/toggleExpanded`, { id: item.id }, { root: true });
  }
  showTrash() {
    const activeSubNavigation = this.rootState.preferences.allPreferences?.activeSubNavigation;

    if (!["businesses", "grids", "processes", "players", "options"].includes(activeSubNavigation)) return;
    const prevValue = this.rootState[mapScopeToStore[activeSubNavigation]].showTrash;
    if (!prevValue) this.dispatch("smartSearch/setSmartSearchPrevScope", HotkeyScopes.Trash, { root: true });

    return this.dispatch(`${mapScopeToStore[activeSubNavigation]}/toggleTrash`, true, { root: true });
  }
  hideTrash() {
    const activeSubNavigation = this.rootState.preferences.allPreferences?.activeSubNavigation;
    const prevScope = this.rootState.smartSearch.smartSearchPrevScope;

    if (!["businesses", "grids", "processes", "players", "options"].includes(activeSubNavigation)) return;
    if (prevScope === HotkeyScopes.Trash)
      this.dispatch("smartSearch/setSmartSearchPrevScope", activeSubNavigation, { root: true });
    return this.dispatch(`${mapScopeToStore[activeSubNavigation]}/toggleTrash`, false, { root: true });
  }
  restore() {
    const activeSubNavigation = this.rootState.preferences.allPreferences?.activeSubNavigation;

    const store = mapScopeToStore[activeSubNavigation];
    if (!["processes", "players", "options"].includes(activeSubNavigation)) return;
    const ids = this.rootState[store].selectedTrashItems;
    this.dispatch("smartSearch/setSmartSearchPrevScope", activeSubNavigation, { root: true });
    this.dispatch(`${store}/setSelectedTrashItems`, [], { root: true });

    return this.dispatch(`${store}/restore`, { ids }, { root: true });
  }
  newPlayer() {
    const playerId = this.rootState.players?.activePlayer?.id;
    if (!playerId) return;
    this.dispatch("players/addPlayer", { id: playerId }, { root: true });
  }
  renamePlayer() {
    const playerId = this.rootState.players?.activePlayer?.id;
    if (!playerId) return;
    this.dispatch("players/setActiveItem", { id: playerId, editMode: true }, { root: true });
  }
  editSheet() {
    const activeSheet = this.rootState.sheets.activeSheet;
    const sheet = this.rootGetters["sheets/getSheetById"](activeSheet.id);
    if (sheet.parents[1] === TreeDefaultNodeIds.Trash) return;
    return this.dispatch("sheets/setActiveSheet", { id: activeSheet.id, editMode: true }, { root: true });
  }
  fontSize() {
    const parameters = this.rootState.smartSearch.parameters;
    const size = parameters && parameters[0];
    if (!size || isNaN(size) || size > 72 || size < 8) return;
    this.dispatch("rangeStyle/updateRangeStyle", { data: { fontSize: size } }, { root: true });
  }
  width() {
    const parameters = this.rootState.smartSearch.parameters;
    const size = parameters && parameters[0];
    const mainRange = this.rootGetters["cells/mainRange"]?.range;
    if (!size || isNaN(size) || size > 1000 || !mainRange) return;
    const rowIndex = this.current.design.rowMap["Size"].index;
    const range = [mainRange[0], rowIndex, mainRange[2], rowIndex];
    this.dispatch("cells/updateSelectedCells", { range, value: size }, { root: true });
  }
  height() {
    const parameters = this.rootState.smartSearch.parameters;
    const size = parameters && parameters[0];
    const mainRange = this.rootGetters["cells/mainRange"]?.range;
    if (!size || isNaN(size) || size > 1000 || !mainRange) return;
    const columnIndex = this.current.design.columnMap["Size"].index;
    const range = [columnIndex, mainRange[1], columnIndex, mainRange[3]];
    this.dispatch("cells/updateSelectedCells", { range, value: size }, { root: true });
  }
  async addPlayer() {
    const parameterIds = this.rootState.smartSearch.parameterIds.map((item) => item.id);

    const [, columnId] = splitCellId(this.activeCellId);
    const column = this.columnMap[columnId];
    const index = column.index;

    const newCellIndex = calculateNewPlayerPosition(this.rootState.cells, index);

    const delta = this.current.type === ProcessItemTypes.Normal ? 1 : 2;
    // Create an array of objects with playerId and incremented index for each playerId
    const playerData = parameterIds.map((playerId, index) => ({
      playerId, // each playerId from parameterIds
      playerIndex: newCellIndex + index * delta, // increment index for each playerId
    }));

    const actionName = this.current.type === ProcessItemTypes.Normal ? "addPlayer" : "addPostingPlayer";
    return this.dispatch(
      `cells/${actionName}`,
      { ids: playerData.map((x) => x.playerId), index: newCellIndex },
      { root: true }
    );
  }
  editPlayer() {
    const parameterIds = this.rootState.smartSearch.parameterIds.map((item) => item.id);
    const playerId = parameterIds[0];

    let [, columnId] = splitCellId(this.activeCellId);

    columnId = columnId.includes("-Minus") ? columnId.replace("-Minus", "-Plus") : columnId;

    this.dispatch(
      "cells/editLink",
      {
        cellId: `Player_${columnId}`,
        newPlayerId: playerId,
        type: "player",
      },
      { root: true }
    );
    this.dispatch(
      "cells/editCellContent",
      {
        id: `Player_${columnId}`,
        value: this.rootGetters["players/getPlayerById"](playerId)?.title,
      },
      { root: true }
    );
  }
  addView() {
    const parameterIds = this.rootState.smartSearch.parameterIds;
    const parameters = this.rootState.smartSearch.parameters;
    if (parameters.length < 2 || parameterIds.length < 1) return;

    const title = parameters[0];
    const icon = parameterIds[0].icon;
    this.dispatch(
      "views/addView",
      {
        title,
        icon,
        designId: this.current.id,
      },
      { root: true }
    );
  }
  hideRow() {
    const mainRange = this.rootGetters["cells/mainRange"]?.range;
    if (!mainRange) return;

    const columnIndex = this.current.design.columnMap["Show"].index;
    const range = [columnIndex, mainRange[1], columnIndex, mainRange[3]];
    this.dispatch("cells/updateSelectedCells", { range, value: "0" }, { root: true });
  }
  hideColumn() {
    const mainRange = this.rootGetters["cells/mainRange"]?.range;
    if (!mainRange) return;

    const rowIndex = this.current.design.rowMap["Show"].index;
    const range = [mainRange[0], rowIndex, mainRange[2], rowIndex];
    this.dispatch("cells/updateSelectedCells", { range, value: "0" }, { root: true });
  }
  autoLayout() {
    this.dispatch("customFlow/autoLayout", null, { root: true });
  }
  toggleProcessFlowLines() {
    this.dispatch("customFlow/toggleVerticalLines", null, { root: true });
  }
  addProcessFlowArrow() {
    this.dispatch("customFlow/setNewArrowActive", null, { root: true });
  }
  changeCircleType(shapeType) {
    this.dispatch("customFlow/setShapeType", shapeType, { root: true });
  }
  openTable() {
    hotkeys.trigger("ctrl+t", HotkeyScopes.ProcessDesigner);
  }
  toggleGridlines() {
    this.dispatch("cells/toggleGridlines", null, { root: true });
  }
  setDataType(dataType) {
    const mainRange = this.rootGetters["cells/mainRange"]?.range;
    if (!mainRange) return;
    this.dispatch(
      "rangeStyle/updateRangeStyle",
      {
        data: { dataType },
      },
      { root: true }
    );
  }
  openNameManager() {
    this.dispatch("table/setNameManagerVisibility", true, { root: true });
  }
  openAddNameRange() {
    this.dispatch("table/setAddNamedRangeVisibility", true, { root: true });
  }
  addProcessFromCycle() {
    this.dispatch("cells/setCreateSapDialogVisibility", true, { root: true });
  }
  renameTable() {
    const parameters = this.rootState.smartSearch.parameters;
    const name = parameters[0];
    const activeTable = this.rootState.table.activeTable;
    if (!activeTable || !tableTitleValidation(name)) return;
    const updatedTable = { ...activeTable, tableName: name };
    this.dispatch("table/updateTableDetails", { updatedTable, oldTable: activeTable }, { root: true });
  }
  toggleAutoAppend() {
    const activeTable = this.rootState.table.activeTable;
    if (!activeTable) return;
    let value = !activeTable.allowDynamicExpansion;
    const updatedTable = { ...activeTable, allowDynamicExpansion: value };
    this.dispatch("table/updateTableDetails", { updatedTable, oldTable: activeTable }, { root: true });
  }
  toggleIsColumnFormula() {
    const activeTable = this.rootState.table.activeTable;
    if (!activeTable) return;
    let value = !activeTable.applyFormulaToEntireColumn;
    const updatedTable = { ...activeTable, applyFormulaToEntireColumn: value };
    this.dispatch("table/updateTableDetails", { updatedTable, oldTable: activeTable }, { root: true });
  }
  sortDesc() {
    const activeTable = this.rootState.table.activeTable;
    if (!activeTable) return;
    this.dispatch("table/sortTable", "desc", { root: true });
  }
  sortAsc() {
    const activeTable = this.rootState.table.activeTable;
    if (!activeTable) return;
    this.dispatch("table/sortTable", "asc", { root: true });
  }
  async filter() {
    const activeTable = this.rootState.table.activeTable;
    if (!activeTable) return;
    const parameters = this.rootState.smartSearch.parameters;
    if (parameters.length === 0) return this.dispatch("table/setFilterDialogVisible", true, { root: true });
    let cellId = this.rootState.table.selectedCellId;
    const range = this.rootGetters["table/columnRange"](cellId);
    // cellId of header of that column is required
    cellId = this.rootGetters["cells/rangeIds"](range)[1];

    const filterCriteria = {
      cellId,
      type: null,
      condition1: null,
      value1: null,
      condition2: null,
      value2: null,
      logicalOperator: null,
      selectedValues: parameters,
    };

    await this.dispatch("table/updateTableFilter", { table: activeTable, filter: filterCriteria }, { root: true });
    hotkeys.trigger("down", HotkeyScopes.ProcessDesigner);
    hotkeys.trigger("up", HotkeyScopes.ProcessDesigner);
  }
  openFilter() {
    const activeTable = this.rootState.table.activeTable;
    if (!activeTable) return;
    this.dispatch("table/setFilterDialogVisible", true, { root: true });
  }
  async clearFilter() {
    const activeTable = this.rootState.table.activeTable;
    if (!activeTable) return;
    await this.dispatch("table/deleteTableFilter", { table: activeTable }, { root: true });
  }
  startStyles() {
    this.dispatch("styleCorner/startStyles", null, { root: true });
  }
  loadSatellite() {
    this.dispatch("satelliteCorner/load", null, { root: true });
  }
}

export const calculateNewPlayerPosition = (cellState, index, isBefore = false) => {
  const current = cellState.current;
  const columns = current?.design.columns;

  const columnId = current.design.columns[index].id;

  const firstPlayerIndex = columns.findIndex((item) => item.type === ColumnTypes.Player);
  const isMinusColumn = columnId.includes("-Minus");
  let newIndexOffset;
  if (firstPlayerIndex < 0) {
    newIndexOffset = columns.length - index;
  } else if (index < firstPlayerIndex) {
    newIndexOffset = firstPlayerIndex - index;
  } else if (firstPlayerIndex === -1) {
    newIndexOffset = 1;
  } else if (isBefore && isMinusColumn) {
    newIndexOffset = -1;
  } else if (isBefore) {
    newIndexOffset = 0;
  } else if (isMinusColumn) {
    newIndexOffset = 1;
  } else if (current.type === ProcessTypes.Posting) {
    newIndexOffset = 2;
  } else {
    newIndexOffset = 1;
  }

  return index + newIndexOffset;
};
